import React from "react";
import { getAvatarColor, getInitials } from "../../utilities/avatar";
import "./PlayerAvatarStrip.css";

// Maximum number of avatars to render (prevents DOM bloat with 300+ players)
const MAX_VISIBLE_AVATARS = 40;

// Avatar size in pixels
const AVATAR_SIZE = 40;

/**
 * Single avatar bubble component.
 */
function AvatarBubble({ player, size = AVATAR_SIZE }) {
    const { nickname, avatarThumb } = player;
    const initials = getInitials(nickname);
    const bgColor = getAvatarColor(nickname);

    const style = {
        width: size,
        height: size,
        minWidth: size,
        backgroundColor: avatarThumb ? "transparent" : bgColor
    };

    return (
        <div
            className="avatar-bubble"
            style={style}
            title={nickname}
        >
            {avatarThumb ? (
                <img src={avatarThumb} alt={nickname} />
            ) : (
                <span className="avatar-bubble-initials">{initials}</span>
            )}
        </div>
    );
}

/**
 * Overflow indicator showing "+X more" players.
 */
function OverflowBubble({ count, size = AVATAR_SIZE }) {
    const style = {
        width: size,
        height: size,
        minWidth: size
    };

    return (
        <div
            className="avatar-bubble avatar-bubble-overflow"
            style={style}
            title={`${count} more players`}
        >
            <span className="avatar-bubble-initials">+{count}</span>
        </div>
    );
}

/**
 * PlayerAvatarStrip - A horizontal strip of player avatar bubbles.
 *
 * Props:
 *   players: Array of { playerId, nickname, avatarThumb } objects
 *   maxVisible: Maximum avatars to display before showing overflow (default: 40)
 *   size: Avatar size in pixels (default: 40)
 *
 * Features:
 *   - Shows circular avatars with photos or initials
 *   - Limits DOM nodes for large player counts
 *   - Shows "+X" overflow indicator when exceeding maxVisible
 *   - Uses consistent colors for initials based on nickname
 */
function PlayerAvatarStrip({
    players = [],
    maxVisible = MAX_VISIBLE_AVATARS,
    size = AVATAR_SIZE
}) {
    if (!players || players.length === 0) {
        return null;
    }

    // Take the most recent players (end of array) to show newest joins
    const overflowCount = Math.max(0, players.length - maxVisible);
    const visiblePlayers = overflowCount > 0
        ? players.slice(-maxVisible)
        : players;

    return (
        <div className="player-avatar-strip">
            <div className="player-avatar-strip-container">
                {overflowCount > 0 && (
                    <OverflowBubble count={overflowCount} size={size} />
                )}
                {visiblePlayers.map((player) => (
                    <AvatarBubble
                        key={player.playerId}
                        player={player}
                        size={size}
                    />
                ))}
            </div>
        </div>
    );
}

export default PlayerAvatarStrip;
